import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const STAGES = [
  "Understanding your vision",
  "Building flavor progression",
  "Developing recipes",
  "Calculating portions",
  "Analyzing food cost",
  "Designing the plate",
  "Building your menu",
];

export default function GenerationOverlay({ active, done, onReveal, menuName }) {
  const [stage, setStage] = useState(0);
  const [plated, setPlated] = useState(false);

  useEffect(() => {
    if (!active) { setStage(0); setPlated(false); return; }
    const t = setInterval(() => setStage((s) => (s < STAGES.length - 1 ? s + 1 : s)), 1400);
    return () => clearInterval(t);
  }, [active]);

  useEffect(() => {
    if (done) {
      setStage(STAGES.length - 1);
      const t = setTimeout(() => setPlated(true), 500);
      return () => clearTimeout(t);
    }
  }, [done]);

  if (!active && !done) return null;

  return (
    <AnimatePresence>
      <motion.div
        data-testid="generation-overlay"
        className="fixed inset-0 z-[100] flex items-center justify-center grain"
        style={{ background: "rgba(6,6,6,0.94)", backdropFilter: "blur(12px)" }}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      >
        {!plated ? (
          <div className="text-center px-6">
            <div className="relative mx-auto mb-10 h-40 w-40">
              <motion.div className="absolute inset-0 rounded-full"
                style={{ boxShadow: "0 0 60px rgba(212,175,55,0.35)" }}
                animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 2, repeat: Infinity }} />
              <svg viewBox="0 0 100 100" className="h-40 w-40">
                <motion.circle cx="50" cy="50" r="44" fill="none" stroke="#D4AF37" strokeWidth="1.5"
                  strokeDasharray="276" initial={{ strokeDashoffset: 276 }}
                  animate={{ strokeDashoffset: 0 }} transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }} />
                <circle cx="50" cy="50" r="34" fill="none" stroke="rgba(212,175,55,0.25)" strokeWidth="0.8" />
              </svg>
            </div>
            <AnimatePresence mode="wait">
              <motion.p key={stage} className="font-serif text-2xl md:text-3xl gold-text"
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                {STAGES[stage]}
              </motion.p>
            </AnimatePresence>
            <div className="mt-8 flex justify-center gap-1.5">
              {STAGES.map((_, i) => (
                <div key={i} className={`h-1 w-8 rounded-full transition-colors ${i <= stage ? "bg-gold" : "bg-white/10"}`} />
              ))}
            </div>
          </div>
        ) : (
          <motion.div className="text-center px-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <motion.svg viewBox="0 0 200 200" className="mx-auto h-56 w-56"
              initial={{ scale: 0.8 }} animate={{ scale: 1 }} transition={{ duration: 0.8, ease: "easeOut" }}>
              <motion.circle cx="100" cy="100" r="78" fill="none" stroke="#D4AF37" strokeWidth="2"
                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.2, ease: "easeInOut" }} />
              <motion.circle cx="100" cy="100" r="58" fill="none" stroke="rgba(212,175,55,0.4)" strokeWidth="1"
                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.2, delay: 0.3 }} />
              {[0, 72, 144, 216, 288].map((a, i) => {
                const rad = (a * Math.PI) / 180;
                const x = 100 + 68 * Math.cos(rad); const y = 100 + 68 * Math.sin(rad);
                return (
                  <motion.circle key={i} cx={x} cy={y} r="4" fill="#F3D566"
                    initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.9 + i * 0.12 }} />
                );
              })}
            </motion.svg>
            <motion.h2 className="mt-8 font-serif text-4xl md:text-5xl gold-text tracking-wide"
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4 }}>
              YOUR MENU IS PLATED.
            </motion.h2>
            {menuName && (
              <motion.p className="mt-3 text-white/60 font-sans"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.7 }}>
                {menuName}
              </motion.p>
            )}
            <motion.button data-testid="view-menu-btn" onClick={onReveal}
              className="btn-gold mt-10 px-10 py-4 text-sm uppercase tracking-widest"
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.9 }}>
              View Menu
            </motion.button>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
